import { useState, useMemo } from 'react';
import { Copy, ArrowLeftRight, FileUp } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Switch } from '@/components/ui/Switch';
import { copyToClipboard, readFileAsText } from '@/lib/utils';
import { toast } from 'sonner';

export function Base64() {
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [input, setInput] = useState('Hello, Utility OS');
  const [urlSafe, setUrlSafe] = useState(false);

  const output = useMemo(() => {
    if (!input) return '';
    try {
      if (mode === 'encode') {
        const enc = btoa(unescape(encodeURIComponent(input)));
        return urlSafe ? enc.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') : enc;
      } else {
        const padded = urlSafe
          ? input.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((input.length + 3) % 4)
          : input;
        return decodeURIComponent(escape(atob(padded)));
      }
    } catch {
      return '⚠ Invalid input';
    }
  }, [input, mode, urlSafe]);

  return (
    <div>
      <ToolHeader
        toolId="base64"
        title="Base64 encode / decode"
        description="Encode text to Base64 or decode Base64 back to text. Supports URL-safe variants."
      />

      <Tabs value={mode} onValueChange={(v) => setMode(v as 'encode' | 'decode')}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <TabsList>
            <TabsTrigger value="encode">Encode</TabsTrigger>
            <TabsTrigger value="decode">Decode</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-fg-muted">URL-safe</span>
            <Switch checked={urlSafe} onChange={setUrlSafe} />
          </div>
        </div>

        <TabsContent value="encode">
          <BodyPanel
            inputLabel="Plain text"
            outputLabel="Base64"
            input={input}
            output={output}
            onChange={setInput}
            allowFile
            mode={mode}
          />
        </TabsContent>
        <TabsContent value="decode">
          <BodyPanel
            inputLabel="Base64"
            outputLabel="Plain text"
            input={input}
            output={output}
            onChange={setInput}
            mode={mode}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BodyPanel({
  inputLabel,
  outputLabel,
  input,
  output,
  onChange,
  allowFile,
  mode,
}: {
  inputLabel: string;
  outputLabel: string;
  input: string;
  output: string;
  onChange: (v: string) => void;
  allowFile?: boolean;
  mode: 'encode' | 'decode';
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="!p-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
          <div className="text-xs font-medium text-fg-muted">{inputLabel}</div>
          <div className="flex items-center gap-1">
            {allowFile && (
              <label className="cursor-pointer inline-flex items-center gap-1 px-2 h-7 text-xs rounded-md text-fg-muted hover:bg-surface-hover">
                <FileUp size={11} />
                File
                <input
                  type="file"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (mode === 'encode') {
                      // Read as binary for files
                      const buf = await f.arrayBuffer();
                      const bytes = new Uint8Array(buf);
                      let bin = '';
                      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
                      onChange(bin);
                    } else {
                      onChange(await readFileAsText(f));
                    }
                  }}
                />
              </label>
            )}
            <Button size="sm" variant="ghost" onClick={() => onChange('')}>
              Clear
            </Button>
          </div>
        </div>
        <Textarea
          mono
          value={input}
          onChange={(e) => onChange(e.target.value)}
          className="!border-0 !rounded-t-none min-h-[300px] !bg-transparent focus-visible:!ring-0"
        />
      </Card>

      <Card className="!p-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
          <div className="text-xs font-medium text-fg-muted">{outputLabel}</div>
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              const ok = await copyToClipboard(output);
              if (ok) toast.success('Copied');
            }}
            disabled={!output}
          >
            <Copy size={11} />
          </Button>
        </div>
        <pre className="font-mono text-[13px] leading-relaxed p-4 overflow-auto min-h-[300px] max-h-[400px] break-all whitespace-pre-wrap">
          <code>{output}</code>
        </pre>
      </Card>
    </div>
  );
}

export default Base64;
